import { ReactElement } from 'react';
import create from 'zustand';

import { Api } from './api';
import {
  auditReportCall,
  getAuditedWorksheetDetails,
  getAuditorWorksheetDetails,
} from './audit-report-api';
import { DateInline, Tags, Text } from './controls';
import { DateRange, isoDateTimeString, utcDateString } from './date-time';
import { Provider, knownCodingstates } from './domain';
import { FormApi, formApi, providerField } from './form';
import { HBox } from './layout';
import { PageApi } from './paged-table';
import {
  Filters,
  SearchPredicate,
  VisitSearchField,
  VisitSearchQuery,
  VisitSortField,
  auditReviewAccountField,
  auditReviewAuditedField,
  auditReviewAuditorField,
  auditReviewIdField,
  auditReviewProviderField,
  patientFirstField,
  patientLastField,
  serviceDateField,
  worksheetAuditDateField
} from './visit-queue';
import { Feature } from './workflow-api';
import { flagCommentsCriteria, flagCriteria, flagDescriptionCriteria, flagEmployeeLastCriteria } from './visit-search-api';
export interface VisitSearchFieldCriteria {
  field: VisitSearchField;
  form?: FormApi;
  color?: string;
  label?: string;
  icon?: ReactElement;
  editable?: boolean;
  dateTime?: boolean;
  api?: AuditReviewSearchApi;
  startDate?: Date;
  endDate?: Date;
  valueLabel?: (v?: any) => string;
  editor?: (c: VisitSearchFieldCriteria) => JSX.Element;
  variant?: string;
  onChange?: (values: any) => void;
}

type VisitSearchConfiguration = {
  query: MyAuditSearchQuery;
  fields: VisitSearchFieldCriteria[];
  filters: VisitSearchFieldCriteria[];
  sort: VisitSearchFieldCriteria[];
  features?: Feature[];
};

type VisitSearchData = VisitSearchConfiguration & {
  selectedFilter: VisitSearchFieldCriteria;
  openFilter: boolean;
  period: PeriodSearch;
  advancedActive: boolean;
  providers: Provider[];
  stateFilters: { label: string; name: string }[];
  pendingNavigation?: boolean;
  result?: any;
  columns?: any;
  isAudited?: boolean;
};

interface PeriodSearch {
  period?: DateRange;
  search?: string;
}

type VisitSearchActions = {
  setQuery(v: Partial<VisitSearchQuery>): void;
  updateOrAddFilter(field: VisitSearchField): VisitSearchField;
  updateOrAddSort(field: VisitSearchField): VisitSearchField;
  updateColumnSort(field: VisitSearchField): VisitSearchField;
  sendRawQuery(
    filter: any,
    props: { auditId?: string[]; providerId?: string[]; auditedId?: string[] }
  ): void;
  setAudited(): void;
  search(): void;
  reset(): void;
  setServiceDate(range: DateRange): void;
  hasFilter: (c: VisitSearchFieldCriteria) => boolean;
  openFilter: (c: VisitSearchFieldCriteria) => void;
  closeFilter: () => void;
  deleteFilter: (c: VisitSearchFieldCriteria) => void;
  addFilter: (c: VisitSearchFieldCriteria) => void;
  updateFilter: (c: VisitSearchFieldCriteria) => void;
  addSort: (c: VisitSearchFieldCriteria) => void;
  updateSort: (c: VisitSearchFieldCriteria) => void;
  hasSort: (c: VisitSearchFieldCriteria) => boolean;
  deleteSort: (c: VisitSearchFieldCriteria) => void;
};

type VisitSearchForms = {
  predicate: FormApi<{ predicate: 'OR' | 'AND' }>;
};
export type AuditReviewSearchApi = Api<
  VisitSearchConfiguration,
  VisitSearchData,
  VisitSearchActions,
  VisitSearchForms
>;

function searchFieldCriteria(
  field: VisitSearchField,
  props: Partial<VisitSearchFieldCriteria>
): Partial<VisitSearchFieldCriteria> {
  return {
    field,
    ...props,
  };
}

function textForm(
  criteria: Partial<VisitSearchFieldCriteria>
): VisitSearchFieldCriteria {
  const { field } = criteria;

  let selectedField: VisitSearchFieldCriteria;

  const form = formApi({
    values: {
      control: '',
    },
    fields: {
      control: {
        id: (field?.fieldName || 'text') + '-input',
        required: true,
        label: criteria.label,
        autoFocus: true,
        onEnter: () => {
          form.submit();
        },
      },
    },
    valid: () => true,
    onSubmit: (v) => {
      field.stringFilter = (v.control || '').trim();
      if (selectedField && form.valid.getState().value) {
        selectedField.api.actions.addFilter(selectedField);
      }
    },
  });
  const { control } = form.controls;
  return {
    ...criteria,
    ...{
      form,
      control,
      valueLabel: () => control.value.getState().value || '',
      editor: (v: VisitSearchFieldCriteria) => {
        selectedField = v;
        return <Text {...control} />;
      },
    },
  } as VisitSearchFieldCriteria;
}


function personForm(
  criteria: Partial<VisitSearchFieldCriteria>
): VisitSearchFieldCriteria {
  const { field } = criteria;

  let selectedField: VisitSearchFieldCriteria;

  const form = formApi({
    values: {
      control: [],
    },
    fields: {
      control: providerField([], {
        id: (field?.fieldName || 'text') + '-input',
        required: true,
        label: criteria.label,
        autoFocus: true,
        style: { width: undefined },
        onEnter: () => {
          form.submit();

          if (selectedField && form.valid.getState().value) {
         //  selectedField.field?.sortOrder && delete selectedField.field.sortOrder;
            selectedField.api.actions.addFilter(selectedField);
          }
        },
      }),
    },
    valid: () => true,
    onSubmit: (v) => {
      field.stringFilter = (v.control || []).map((u) => u.lastName);
    },
  });
  const { control } = form.controls;
  return {
    ...criteria,
    ...{
      form,
      control,
      // valueLabel: () => control.value.getState().value && control.value.getState().value.lastName || '',
      valueLabel: () =>
        (control.value.getState().value || []).map((v) => v.label).join(','),
      editor: (v: VisitSearchFieldCriteria) => {
        selectedField = v;
        if (v.api) {
          control.list.setState({
            value: v.api.data.getState().providers,
          });
        }
        return <Tags {...control} />;
      },
    },
  } as VisitSearchFieldCriteria;
}

function dateForm(
  criteria: Partial<VisitSearchFieldCriteria>
): VisitSearchFieldCriteria {
  const { dateTime, field } = criteria;

  let selectedCriteria: VisitSearchFieldCriteria;
  const onEnter = () => {
    form.submit();
    if (selectedCriteria && form.valid.getState().value) {
      //selectedCriteria.field?.sortOrder && delete selectedCriteria.field.sortOrder;
      selectedCriteria.api.actions.addFilter(selectedCriteria);
    }
  };

  const form = formApi({
    values: {
      start: null,
      end: new Date(),
    },
    fields: {
      start: {
        id: (field?.fieldName || 'text') + 'start-input',
        autoFocus: true,
        required: true,
        label: 'Start',
        onEnter,
      },
      end: {
        id: (field?.fieldName || 'text') + 'end-input',
        required: true,
        label: 'End',
        onEnter,
      },
    },
    valid: () => true,
    onSubmit: (v) => {
      const field = selectedCriteria.field;
      field.startDate = dateTime
        ? isoDateTimeString(v.start || new Date())
        : utcDateString(v.start || new Date());
      field.endDate = dateTime
        ? isoDateTimeString(v.end || new Date())
        : utcDateString(v.end || new Date());
    },
  });

  const { start, end } = form.controls;
  return {
    ...criteria,
    ...{
      form,
      control: end,
      valueLabel: (v: VisitSearchFieldCriteria) => {
        const c = selectedCriteria || v;
        return `${c.field.startDate || ''} to ${c.field.endDate || ''}`;
      },
      editor: (v: VisitSearchFieldCriteria) => {
        selectedCriteria = v;
        const { startDate, endDate } = v;
        if (startDate) {
          start.value.setState({ value: startDate });
        }
        if (endDate) {
          end.value.setState({ value: endDate });
        }
        return (
          <HBox>
            <DateInline {...start} />
            <DateInline {...end} />
          </HBox>
        );
      },
    },
  } as VisitSearchFieldCriteria;
}


const accountCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(auditReviewAccountField, {
      ...{ label: 'Account', editable: true },
      ...props,
    })
  );



const serviceDateCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  dateForm(
    searchFieldCriteria(serviceDateField, {
      ...{ label: 'Service', editable: true },
      ...props,
    })
  );


const auditIdCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(auditReviewIdField, {
      ...{ label: 'Audit Rule', editable: true },
      ...props,
    })
  );


const worksheetAuditDateCriteria = (
  props?: Partial<VisitSearchFieldCriteria>
) =>
  dateForm(
    searchFieldCriteria(worksheetAuditDateField, {
      ...{ label: 'Audit Date', editable: true },
      ...props,
    })
  );

const worksheetAuditorCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(auditReviewAuditorField, {
      ...{ label: 'Auditor', editable: true },
      ...props,
    })
  );

const worksheetAuditedCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(auditReviewAuditedField, {
      ...{ label: 'Audited', editable: true },
      ...props,
    })
  );




const auditProviderCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  personForm(
    searchFieldCriteria(auditReviewProviderField, {
      ...{ label: 'Provider', editable: true },
      ...props,
    })
  );




const serviceDate = serviceDateCriteria();

export const myAuditWorksheetCriteria = [
  worksheetAuditDateCriteria(),
  accountCriteria(),
  worksheetAuditorCriteria(),
  worksheetAuditedCriteria(),
  auditIdCriteria(),
  auditProviderCriteria(),
  flagCriteria(),
  flagDescriptionCriteria(),
  flagCommentsCriteria(),
  flagEmployeeLastCriteria(),
];



function auditSearchConfiguration(
  conf?: Partial<VisitSearchConfiguration>
): VisitSearchConfiguration {
  return {
    ...{
      query: {},
      fields: myAuditWorksheetCriteria as any,
      filters: [],
      sort: [],
    },
    ...conf,
  };
}

const data = create<VisitSearchData>(() => ({
  fields: [],
  query: {},
  filters: [],
  sort: [],
  selectedFilter: null,
  openFilter: false,
  period: null,
  advancedActive: false,
  providers: [],
  stateFilters: knownCodingstates,
  pendingNavigation: false,
  result: null,
  columns: null,
  isAudited: true,
}));

type MyAuditWorksheetResponse = {
  departmentCodes?: string[] | string;
  domain?: string[] | any;
  endDate: string;
  locationCodes?: string[] | string;
  page: number;
  pageSize: number;
  result?: MyAuditWorksheetResult[];
  startDate: string;
  success: boolean;
  totalCount: number;
};


type MyAuditWorksheetResult = {
  accountNumber?: string | string[];
  activeCopyId?: string;
  archiveId?: string;
  auditList?: any;
  audited?: any;
  auditor?: any;
  codingQueue?: any;
  componentToken?: string;
  createdDate?: string;
  createdLocal?: Date | number;
  createdWho?: string;
  departmentCode?: string;
  fromWorkflow?: string;
  locationCode?: string;
  mrn?: string;
  owningProvider?: any;
  serviceDate?: Date | number;
  toWorkflow?: string;
  totalChangePoints: number;
  totalPoints?: number;
  totalPossiblePoints?: number;
  worksheetId?: string;
  worksheetType?: string;
};

export type MyAuditSearchQuery = {
  locationCode?: string;
  departmentCode?: string;
  pageNumber?: number;
  page?: number;
  pageSize?: number;
  queryString?: string;
  filter?: Filters;
  sort?: VisitSortField[];
  auditId?: string | string[];
  providerId?: string | string[];
  auditedId?: string | string[];
  startDate?: string;
  endDate?: string;
};

export function auditWorksheetSearchApi(
  page?: PageApi,
  isAudit?: boolean
): any {
  const configuration = auditSearchConfiguration();

  data.setState({ fields: configuration.fields });

  const checkPayload = () => {
    if (
      data.getState().query.locationCode &&
      data.getState().query.departmentCode &&
      data.getState().query.startDate &&
      data.getState().query.endDate
    ) {
      return true;
    }
    return false;
  };

  const search = async (props?: {
    auditId?: string[];
    providerId?: string[];
    auditedId?: string[];
  }) => {
    const isAudited = data.getState().isAudited;
    refreshQuery();
    const query = data.getState().query;
    query.auditId = props?.auditId ? props.auditId : query.auditId;
    query.providerId = props?.providerId ? props.providerId : query.providerId;
    query.auditedId = props?.auditedId ? props.auditedId : query.auditedId;
    
    
    if (!query.locationCode || !query.endDate) {
      return Promise.resolve({}) as any;
    }

    let resp: MyAuditWorksheetResponse =
      isAudited === true
        ? checkPayload() && (await getAuditedWorksheetDetails(query))
        : data.getState().query.endDate &&
          (await getAuditorWorksheetDetails(query));

    data.setState({
      columns: resp?.domain?.['Aggregation Categories']
        ? resp?.domain?.['Aggregation Categories']
        : null,
    });

    data.setState({ result: resp });
  };

  const searchDrillDown = async (props?: {
    auditId?: string[];
    providerId?: string[];
    auditedId?: string[];
  }) => {
    const isAudited = data.getState().isAudited;
    refreshQuery();
    const query = data.getState().query;
    props?.auditId ? (query.auditId = props.auditId) : delete query.auditId;
    props?.providerId
      ? (query.providerId = props.providerId)
      : delete query.providerId;
    props?.auditedId
      ? (query.auditedId = props.auditedId)
      : delete query.auditedId;

    
    if (!query.locationCode) {
      return Promise.resolve({}) as any;
    }
    let resp: MyAuditWorksheetResponse =
      isAudited === true
        ? checkPayload() && (await getAuditedWorksheetDetails(query))
        : data.getState().query.endDate &&
          (await getAuditorWorksheetDetails(query));

    data.setState({
      columns: resp?.domain?.['Categories']
        ? resp?.domain?.['Aggregation Categories']
        : null,
    });
    data.setState({ result: resp });
    
  };

  function hasField(
    list: VisitSearchFieldCriteria[],
    v: VisitSearchFieldCriteria
  ): boolean {
    return list.filter((u) => u.label === v.label).length > 0;
  }

  const hasFilter = (v: VisitSearchFieldCriteria) =>
    hasField(data.getState().filters, v);
  const hasSort = (v: VisitSearchFieldCriteria) =>
    hasField(data.getState().sort, v);

  const refreshFields = () => ({ fields: data.getState().fields.slice() });
  const refreshSort = () => ({ sort: data.getState().sort.slice() });

  function updateOrAdd(
    list: VisitSearchFieldCriteria[],
    field: VisitSearchField
  ): VisitSearchField {
    let item = list.filter((u) => u.field.fieldName === field.fieldName)[0];
    if (!item) {
      item = data
        .getState()
        .fields.filter((u) => u.field.fieldName === field.fieldName)[0];
      if (!item) {
        throw new Error(
          field.fieldName + ' is not in the visit search configuration'
        );
      }
      list.push(item);
    }
    item.field = { ...item.field, ...field };
    return item.field;
  }

  const updateOrAddFilter = (field: VisitSearchField) => {
    const v = updateOrAdd(data.getState().filters, field);
    refreshSort();
    return v;
  };


  const updateOrAddSort = (field: VisitSearchField) => {
    
    const v = updateOrAdd(data.getState().sort, field);
    
    refreshFields();
    return v;
  };

  let currentColumnSort: VisitSearchField;
  let currentPredicate: SearchPredicate = 'OR';

  const refreshQuery = () => {
    const isAudited = data.getState().isAudited;
    const query: MyAuditSearchQuery = data.getState().query || {};
    const predicate = currentPredicate || 'OR';
    const advanced = true;
    const { search } = { ...data.getState().period };

    query.queryString = search ? search : undefined;
    query.startDate = auditReportCall.data.getState().startDate as any;
    query.endDate = auditReportCall.data.getState().endDate as any;

    if (advanced) {
      const filters = data.getState().filters;
      query.sort = data.getState().sort.map((v) => ({
        fieldName: v.field.fieldName,
        sortOrder: isNaN(v.field.sortOrder) ? 1 : v.field.sortOrder,
      }));
      query.filter = {
        predicate,
        fields: filters?.map((v) => ( v?.field ? v.field : (v as any))
         ),
      };
      
    } else {
      query.filter = undefined;

      if (currentColumnSort) {
        query.sort = [
          {
            fieldName: currentColumnSort.fieldName,
            sortOrder: isNaN(currentColumnSort.sortOrder)
              ? 1
              : currentColumnSort.sortOrder,
          },
        ];
      }
    }
    query.filter =
    query.filter && query.filter.fields.length ? query.filter : undefined;
    query.sort = query.sort && query.sort.length ? query.sort : undefined;

   
    data.setState({ query });

    return query;
  };
  const setQuery = (query: VisitSearchQuery) => {
    data.setState({ query: { ...refreshQuery(), ...query } });
  };
  const openFilter = (selectedFilter: VisitSearchFieldCriteria) => {
    data.setState({ selectedFilter, openFilter: true });
  };
  const closeFilter = () => {
    data.setState({ openFilter: false });
  };
  const reset = () => {
    const filters = data.getState().filters;
    const queryTemp = data.getState().query;

    queryTemp.auditId = null;
    queryTemp.auditedId = null;
    queryTemp.providerId = null;

    filters.forEach((v: VisitSearchFieldCriteria) => {
      const c = v?.form?.controls?.control;
      if (c) {
        c.clear();
      }
    });

    data.setState({ filters: [], sort: [], query: queryTemp });
  };

  const removeServiceDate = () => {
    const list = data.getState().filters;
    const filters = list.filter((u) => u.label !== serviceDate.label);
    data.setState({ filters });
  };
  const setServiceDate = (range: DateRange) => {
    if (!range || !range.startDate || !range.endDate) {
      removeServiceDate();
    } else {
      serviceDate.startDate = range.startDate;
      serviceDate.endDate = range.endDate;

      serviceDate.field = {
        fieldName: 'serviceDate',
        startDate: utcDateString(range.startDate),
        endDate: utcDateString(range.endDate),
      };
      updateOrAddFilter(serviceDate.field);
    }
  };

  function sendRawQuery(
    filters: any,
    props?: { auditId?: string[]; providerId?: string[]; auditedId?: string[] }
  ) {
    data.setState({ filters: filters.fields });
    data.setState({ sort: [] });
    searchDrillDown(props);
  }

  function setAudited() {
    let currState = data.getState().isAudited;
    data.setState({ isAudited: !currState });
  }

  const actions: VisitSearchActions = {
    search,
    setAudited,
    hasFilter,
    hasSort,
    sendRawQuery,
    setQuery,
    updateOrAddFilter,
    updateOrAddSort,
    openFilter,
    closeFilter,
    reset,
    setServiceDate,
    updateColumnSort: (v) => {
      currentColumnSort = v;
      return v;
    },
    deleteFilter: (v) => {
      const list = data.getState().filters;
      const filters = list.filter((u) => u.label !== v.label);
      data.setState({ ...{ filters }, ...refreshFields() });
      search();
    },
 
    addFilter: (v) => {
      const filters = data.getState().filters;
      if (!hasField(filters, v)) {
        data.setState({
          ...{ filters: filters.concat({ ...v }) },
          ...refreshFields(),
        });
      }
      search();
      closeFilter();
    },
    updateFilter: (v) => {
      const filters = data.getState().filters;
      filters.forEach((u) => {
        if (u.label === v.label) {
          u.field = { ...u.field, ...v.field };
          
        }
      });
      search();
    },
    deleteSort: (v) => {
      const list = data.getState().sort;
      const sort = list.filter((u) => u.label !== v.label);
      data.setState({ ...{ sort }, ...refreshFields() });
      search();
    },
    addSort: (v) => {
      const sort = data.getState().sort;
      if (!hasField(sort, v)) {
        data.setState({
          ...{ sort: sort.concat({ ...v }) },
          ...refreshFields(),
        });
      }
      search();
    },
    updateSort: (v) => {
      const sort = data.getState().sort;
      sort.forEach((u) => {
        if (u.label === v.label) {
          u.field.sortOrder = v.field.sortOrder;
        }
      });

      search();
    },
  };

  const predicate = formApi<{ predicate: SearchPredicate }>({
    values: { predicate: 'OR' },
    fields: {
      predicate: {
        style: { height: 48 },
        options: [
          { value: 'OR', label: 'Or' },
          { value: 'AND', label: 'And' },
        ],
      },
    },
    onChange: (v) => {
      currentPredicate = v.predicate;
      // refreshQuery();
    },
  });

  const forms = { predicate };

  return {
    configuration,
    data,
    actions,
    forms,
  };
}
